import moment, { type MomentInput } from 'moment-timezone';
import { appConfig } from 'config/app';

export const momentFormatter = 'MM/DD/YYYY';
export const momentISODateFormatter = 'YYYY-MM-DD';
export const momentISODateTimeFormatter = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const momentPrettyDateTimeFormatter = 'MM/DD/YYYY h:mm:ss A';
export const momentPrettyShortDateTimeFormatter = 'MM/DD/YYYY h:mm A';
export const momentFriendlyDateTimeFormatter = 'MMM D, YYYY [at] h:mm:ss A';
export const momentTimeFormatter = 'hh:mm A';
export const momentDateTimeInputFormat = 'YYYY-MM-DD HH:mm:ss.SSS A';

export const getCurrentISOFormattedDate = (): string => {
  return moment().format(momentISODateFormatter);
};

export const getCurrentISOFormattedDateTime = (): string => {
  return moment().format(momentISODateTimeFormatter);
};

export const getPrettyFormattedDateTime = (date: MomentInput): string => {
  if (!date) return null;
  return moment(date).format(momentPrettyDateTimeFormatter);
};

export const getFriendlyFormattedDateTime = (date: MomentInput): string => {
  if (!date) return null;
  return moment(date).format(momentFriendlyDateTimeFormatter);
};

export const getPrettyFormattedShortDateTime = (date: MomentInput): string => {
  if (!date) return null;
  return moment(date).format(momentPrettyShortDateTimeFormatter);
};

export const getPrettyFormattedDateTimeWithTz = (
  date: MomentInput,
  timeZone: string,
  isUTC?: boolean,
): string => {
  if (!date) return null;
  const createMoment = isUTC ? moment.utc : moment;
  return `${createMoment(date)
    .tz(timeZone)
    .format(momentPrettyDateTimeFormatter)} ${
    timeZone && moment().tz(timeZone).format('z')
  }`;
};

export const getPrettyFormattedDate = (date: MomentInput): string => {
  if (!date) return null;
  return moment(date).format(momentFormatter);
};

export const getPrettyFormattedUtcDate = (
  date: MomentInput,
  timeZone?: string,
): string => {
  if (!date) return null;
  const dateMoment = moment.utc(date);
  if (timeZone) dateMoment.tz(timeZone);
  return dateMoment.format(momentFormatter);
};

export const getPrettyFormattedTime = (date: MomentInput): string => {
  if (!date) return null;
  return moment(date).format(momentTimeFormatter);
};

export const getPrettyFormattedUtcTime = (
  date: MomentInput,
  timeZone?: string,
): string => {
  if (!date) return null;
  const dateMoment = moment.utc(date);
  if (timeZone) dateMoment.tz(timeZone);
  return dateMoment.format(momentTimeFormatter);
};

export const getISOFormattedDate = (
  date: MomentInput,
  isUTC = false,
): string => {
  if (!date) return null;
  const createMoment = isUTC ? moment.utc : moment;
  return createMoment(date).format(momentISODateFormatter);
};

export const getISOFormattedDateTime = (date: MomentInput): string => {
  if (!date) return null;
  return moment(date).toISOString();
};

export const getISODateTimeToFormattedDate = (date: MomentInput): string => {
  if (!date || (typeof date === 'string' && date.length < 10)) return null;
  const result = moment(date, momentISODateFormatter);
  return result.isValid() ? result.format(momentFormatter) : null;
};

export const convertUTCtoLocalDate = (utcDate: MomentInput): string => {
  if (!utcDate) return null;

  // Local dev is already in local time so no need for the conversion
  if (appConfig?.appEnv?.includes('local') && typeof utcDate === 'string') {
    return utcDate;
  }

  const utcMoment = moment(utcDate).utc();

  return moment(utcMoment).local().toDate().toISOString();
};

export type GetDateTimeArgs = {
  date: MomentInput;
  time?: string;
  isEnd?: boolean;
  timeZone?: string;
  isUTC?: boolean;
};

export const getDateTimeMoment = (args: GetDateTimeArgs): moment.Moment => {
  const { date, time, timeZone, isEnd, isUTC } = args;
  const dateTimeInput = time
    ? `${getISOFormattedDate(date, true)} ${time}`
    : date;
  const createMoment = isUTC ? moment.utc : moment;
  const result = createMoment(dateTimeInput, momentDateTimeInputFormat);
  if (result.isValid()) {
    if (isEnd) result.endOf('minute');
    if (timeZone) result.tz(timeZone, !isUTC);
    return result;
  }
  return null;
};

export const getDateTime = (args: GetDateTimeArgs): Date => {
  const dateTimeMoment = getDateTimeMoment(args);
  return dateTimeMoment && dateTimeMoment.toDate();
};

type GetJobsiteDateTimeArgs = Omit<GetDateTimeArgs, 'timeZone'> & {
  jobsite?: Pick<GetDateTimeArgs, 'timeZone'>;
};

export const getJobsiteDateTime = (args: GetJobsiteDateTimeArgs): Date => {
  const { jobsite, ...restArgs } = args;
  const { timeZone = 'America/New_York' } = jobsite ?? {};
  return getDateTime({ ...restArgs, timeZone });
};

/**
 * Returns a moment object for the jobsite date and time.
 * If the jobsite has a timeZone, it will use that; otherwise, it defaults
 * to the local time.
 */
export const getJobsiteMoment = (
  args: Omit<GetJobsiteDateTimeArgs, 'isUTC'>,
): moment.Moment => {
  const { jobsite, ...restArgs } = args;
  const { timeZone } = jobsite ?? {};
  const isUTC = !!timeZone;
  return getDateTimeMoment({ ...restArgs, timeZone, isUTC });
};

/**
 * Returns the current moment object for the jobsite.
 * If the jobsite has a timeZone, it will use that; otherwise, it defaults
 * to the local time.
 */
export const getJobsiteMomentNow = (
  jobsite: Pick<GetDateTimeArgs, 'timeZone'>,
): moment.Moment => {
  const { timeZone } = jobsite ?? {};
  const isUTC = !!timeZone;
  const createMoment = isUTC ? moment.utc : moment;
  const result = createMoment(new Date());
  if (timeZone) result.tz(timeZone);
  return result;
};

export const getJobsiteMomentToday = (
  jobsite: Pick<GetDateTimeArgs, 'timeZone'>,
): moment.Moment => {
  const result = getJobsiteMomentNow(jobsite);
  result.startOf('day');
  return result;
};

export const getJobsiteDateTimeNow = (
  jobsite: Pick<GetDateTimeArgs, 'timeZone'>,
): { date: string; time: string; timeZone: string; dateTime: Date } => {
  const jobsiteMomentNow = getJobsiteMomentNow(jobsite);
  return {
    date: jobsiteMomentNow.format(momentISODateFormatter),
    time: jobsiteMomentNow.format(momentTimeFormatter),
    timeZone: jobsiteMomentNow.format('z'),
    dateTime: jobsiteMomentNow.toDate(),
  };
};

export const getTime = (
  args: Pick<GetDateTimeArgs, 'date' | 'timeZone' | 'isUTC'>,
): string => {
  const { date, timeZone, isUTC } = args;
  const createMoment = isUTC ? moment.utc : moment;
  const dateTimeMoment = date && createMoment(date);
  if (timeZone) dateTimeMoment?.tz(timeZone, !isUTC);
  const result = dateTimeMoment?.format(momentTimeFormatter) ?? '';
  return result;
};

export const isAfterToday = (date: MomentInput): boolean => {
  if (!date) return false;
  return moment(date).isAfter(moment().endOf('day'));
};

export const isBeforeToday = (date: MomentInput): boolean => {
  if (!date) return false;
  return moment(date).isBefore(moment().startOf('day'));
};

export const isTodayOrBefore = (date: MomentInput): boolean => {
  if (!date) return false;
  return moment(date).isSameOrBefore(moment().endOf('day'));
};

export function isValidTime(time: string): boolean {
  return time && moment(`2000-01-01 ${time}`).isValid();
}
